/**
 * Option Greeks calculations
 */

import { blackScholesPrice } from './black-scholes';
import {
  calculateD1,
  calculateD2,
  cumulativeNormalDistribution,
  normalProbabilityDensity,
} from './math-utils';
import { OptionParameters, OptionGreeks } from './types';

/**
 * Calculates Delta - sensitivity to underlying price change
 */
export function calculateDelta(params: OptionParameters): number {
  const {
    underlyingPrice: S,
    strikePrice: K,
    timeToExpiry: T,
    riskFreeRate: r,
    volatility: sigma,
    optionType,
  } = params;

  if (T <= 0) {
    if (optionType === 'call') {
      return S > K ? 1 : 0;
    } else {
      return S < K ? -1 : 0;
    }
  }

  const d1 = calculateD1(S, K, T, r, sigma);
  const Nd1 = cumulativeNormalDistribution(d1);

  return optionType === 'call' ? Nd1 : Nd1 - 1;
}

/**
 * Calculates Gamma - sensitivity of delta to underlying price change
 */
export function calculateGamma(params: OptionParameters): number {
  const {
    underlyingPrice: S,
    strikePrice: K,
    timeToExpiry: T,
    riskFreeRate: r,
    volatility: sigma,
  } = params;

  if (T <= 0 || sigma <= 0) {
    return 0;
  }

  const d1 = calculateD1(S, K, T, r, sigma);
  const nd1 = normalProbabilityDensity(d1);

  return nd1 / (S * sigma * Math.sqrt(T));
}

/**
 * Calculates Theta - sensitivity to time decay (per day)
 */
export function calculateTheta(params: OptionParameters): number {
  const {
    underlyingPrice: S,
    strikePrice: K,
    timeToExpiry: T,
    riskFreeRate: r,
    volatility: sigma,
    optionType,
  } = params;

  if (T <= 0) {
    return 0;
  }

  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  const nd1 = normalProbabilityDensity(d1);
  const Nd2 = cumulativeNormalDistribution(d2);
  const NegD2 = cumulativeNormalDistribution(-d2);

  const term1 = -(S * nd1 * sigma) / (2 * Math.sqrt(T));
  const term2 = r * K * Math.exp(-r * T);

  const theta: number =
    optionType === 'call' ? term1 - term2 * Nd2 : term1 + term2 * NegD2;

  // Convert to per-day theta (divide by 365)
  return theta / 365;
}

/**
 * Calculates Vega - sensitivity to volatility change (per 1% vol change)
 */
export function calculateVega(params: OptionParameters): number {
  const {
    underlyingPrice: S,
    strikePrice: K,
    timeToExpiry: T,
    riskFreeRate: r,
    volatility: sigma,
  } = params;

  if (T <= 0) {
    return 0;
  }

  const d1 = calculateD1(S, K, T, r, sigma);
  const nd1 = normalProbabilityDensity(d1);

  // Return vega for 1% volatility change (divide by 100)
  return (S * nd1 * Math.sqrt(T)) / 100;
}

/**
 * Calculates Rho - sensitivity to interest rate change (per 1% rate change)
 */
export function calculateRho(params: OptionParameters): number {
  const {
    underlyingPrice: S,
    strikePrice: K,
    timeToExpiry: T,
    riskFreeRate: r,
    volatility: sigma,
    optionType,
  } = params;

  if (T <= 0) {
    return 0;
  }

  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  const Nd2 = cumulativeNormalDistribution(d2);
  const NegD2 = cumulativeNormalDistribution(-d2);

  const rho: number =
    optionType === 'call'
      ? K * T * Math.exp(-r * T) * Nd2
      : -K * T * Math.exp(-r * T) * NegD2;

  // Return rho for 1% interest rate change (divide by 100)
  return rho / 100;
}

/**
 * Calculates all option Greeks for the given parameters
 *
 * @param params Option parameters including underlying price, strike, time to expiry, etc.
 * @returns Complete set of option Greeks and theoretical price
 */
export function calculateOptionGreeks(params: OptionParameters): OptionGreeks {
  // Validate inputs
  if (params.underlyingPrice <= 0) {
    throw new Error('Underlying price must be positive');
  }
  if (params.strikePrice <= 0) {
    throw new Error('Strike price must be positive');
  }
  if (params.timeToExpiry < 0) {
    throw new Error('Time to expiry cannot be negative');
  }
  if (params.volatility < 0) {
    throw new Error('Volatility cannot be negative');
  }
  if (!['call', 'put'].includes(params.optionType)) {
    throw new Error('Option type must be either "call" or "put"');
  }

  const price = blackScholesPrice(params);
  const delta = calculateDelta(params);
  const gamma = calculateGamma(params);
  const theta = calculateTheta(params);
  const vega = calculateVega(params);
  const rho = calculateRho(params);

  return {
    price,
    delta,
    gamma,
    theta,
    vega,
    rho,
  };
}

/**
 * Validates option parameters and throws descriptive errors for invalid inputs
 */
export function validateOptionParameters(params: OptionParameters): void {
  if (params.underlyingPrice <= 0) {
    throw new Error(
      `Invalid underlying price: ${params.underlyingPrice}. Must be positive.`,
    );
  }
  if (params.strikePrice <= 0) {
    throw new Error(
      `Invalid strike price: ${params.strikePrice}. Must be positive.`,
    );
  }
  if (params.timeToExpiry < 0) {
    throw new Error(
      `Invalid time to expiry: ${params.timeToExpiry}. Cannot be negative.`,
    );
  }
  if (params.volatility < 0) {
    throw new Error(
      `Invalid volatility: ${params.volatility}. Cannot be negative.`,
    );
  }
  if (params.riskFreeRate < -1) {
    throw new Error(
      `Invalid risk-free rate: ${params.riskFreeRate}. Cannot be less than -100%.`,
    );
  }
  if (!['call', 'put'].includes(params.optionType)) {
    throw new Error(
      `Invalid option type: ${params.optionType}. Must be "call" or "put".`,
    );
  }
}
